import { api } from "@/api/client"
import {
  ArenaDetails,
  ArenaMatchCardsPayload,
  ArenaMatchSnapshot,
  ArenaSlotNumber,
  ArenaStatusPayload,
  ArenaSummary,
} from "../types/arena"
import { guardiansLogApiError } from "../utils/guardiansDebugLog"
import { getGameApiErrorMessage } from "./gameApiErrors"
import {
  normalizeArenaDetails,
  normalizeArenaList,
  normalizeArenaMatchCards,
  normalizeArenaMatchSnapshot,
  normalizeArenaStatus,
} from "../utils/arenaNormalize"

export const getArenaList = async (): Promise<ArenaSummary[]> => {
  try {
    const { data } = await api.get<unknown>("/game/arena/list")
    return normalizeArenaList(data)
  } catch (error) {
    guardiansLogApiError("GET /game/arena/list", error)
    throw error
  }
}

export const getArenaDetails = async (
  arenaId: string
): Promise<ArenaDetails | null> => {
  try {
    const { data } = await api.get<unknown>(`/game/arena/${arenaId}`)
    return normalizeArenaDetails(data)
  } catch (error) {
    guardiansLogApiError(`GET /game/arena/${arenaId}`, error)
    throw error
  }
}

export const getArenaMyStatus = async (): Promise<ArenaStatusPayload> => {
  try {
    const { data } = await api.get<unknown>("/game/arena/me/status")
    return normalizeArenaStatus(data)
  } catch (error) {
    guardiansLogApiError("GET /game/arena/me/status", error)
    throw error
  }
}

export const postArenaEnter = async (arenaId: string): Promise<ArenaStatusPayload> => {
  try {
    const { data } = await api.post<unknown>("/game/arena/enter", { arenaId })
    return normalizeArenaStatus(data)
  } catch (error) {
    guardiansLogApiError("POST /game/arena/enter", error)
    throw new Error(getGameApiErrorMessage(error, "Не удалось войти на арену"))
  }
}

export const postArenaLeave = async (): Promise<ArenaStatusPayload> => {
  try {
    const { data } = await api.post<unknown>("/game/arena/leave")
    return normalizeArenaStatus(data)
  } catch (error) {
    guardiansLogApiError("POST /game/arena/leave", error)
    throw new Error(getGameApiErrorMessage(error, "Не удалось выйти с арены"))
  }
}

export const postArenaQueueJoin = async (): Promise<ArenaStatusPayload> => {
  try {
    const { data } = await api.post<unknown>("/game/arena/queue/join")
    return normalizeArenaStatus(data)
  } catch (error) {
    guardiansLogApiError("POST /game/arena/queue/join", error)
    throw new Error(getGameApiErrorMessage(error, "Не удалось встать в очередь"))
  }
}

export const postArenaQueueCancel = async (): Promise<ArenaStatusPayload> => {
  try {
    const { data } = await api.post<unknown>("/game/arena/queue/cancel")
    return normalizeArenaStatus(data)
  } catch (error) {
    guardiansLogApiError("POST /game/arena/queue/cancel", error)
    throw new Error(getGameApiErrorMessage(error, "Не удалось отменить поиск"))
  }
}

export const postArenaClaimSlot = async (payload: {
  matchId: string
  slot: ArenaSlotNumber
}): Promise<void> => {
  try {
    await api.post("/game/arena/slot/claim", payload)
  } catch (error) {
    guardiansLogApiError("POST /game/arena/slot/claim", error)
    throw new Error(getGameApiErrorMessage(error, "Не удалось занять слот"))
  }
}

export const postArenaSelectCard = async (payload: {
  matchId: string
  cardId: string
  abilityId?: string | null
}): Promise<void> => {
  try {
    await api.post("/game/arena/card/select", payload)
  } catch (error) {
    guardiansLogApiError("POST /game/arena/card/select", error)
    throw new Error(getGameApiErrorMessage(error, "Не удалось выбрать карту"))
  }
}

export const postArenaForfeit = async (matchId: string): Promise<void> => {
  try {
    await api.post("/game/arena/forfeit", { matchId })
  } catch (error) {
    guardiansLogApiError("POST /game/arena/forfeit", error)
    throw new Error(getGameApiErrorMessage(error, "Не удалось сдаться"))
  }
}

export const getArenaMatchSnapshot = async (
  matchId: string
): Promise<ArenaMatchSnapshot | null> => {
  try {
    const { data } = await api.get<unknown>(
      `/game/arena/match/${matchId}/snapshot`
    )
    return normalizeArenaMatchSnapshot(data)
  } catch (error) {
    guardiansLogApiError(`GET /game/arena/match/${matchId}/snapshot`, error)
    throw error
  }
}

export const getArenaMatchCards = async (
  matchId: string
): Promise<ArenaMatchCardsPayload | null> => {
  try {
    const { data } = await api.get<unknown>(
      `/game/arena/match/${matchId}/cards`
    )
    return normalizeArenaMatchCards(data)
  } catch (error) {
    guardiansLogApiError(`GET /game/arena/match/${matchId}/cards`, error)
    throw error
  }
}
